// The first email Naima sends a housing provider, exactly as the provider receives it. Plain JavaScript with no imports, so the staff
// dashboard can show it and a Node sending script can build the very same message.

const FROM = 'Naima · Resident Service 360 <naima@residentservice360.bedmaps.com>';
const SIGNATURE_IMAGE = 'https://residentservice360.bedmaps.com/brand/signature-person.png';
const OPT_OUT = 'If this isn\'t for you, reply "no thanks" and I won\'t email again.';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** The first council in "Birmingham, Dudley" (the plan lists a provider's councils with commas); "your area" when there is none. */
function areaOf(council) {
  const first = String(council ?? '').split(',')[0].trim();
  return first || 'your area';
}

const paragraphStyle = 'margin:0 0 14px;font:15px/1.5 Arial,Helvetica,sans-serif;color:#222222;';
const linkStyle = 'color:#5A4A7E;text-decoration:none;';

function signatureHtml() {
  return [
    '<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:22px 0 0;border-collapse:collapse;">',
    '<tr>',
    `<td valign="top" style="padding:0 14px 0 0;"><img src="${SIGNATURE_IMAGE}" width="52" height="52" alt="Resident Service 360" style="display:block;border:0;border-radius:12px;"></td>`,
    '<td valign="top" style="padding:0 0 0 14px;border-left:2px solid #5A4A7E;font:14px/1.45 Arial,Helvetica,sans-serif;color:#222222;">',
    '<div style="font-size:16px;font-weight:bold;color:#2B2438;">Naima</div>',
    '<div style="color:#665E73;">Housing partnerships · Resident Service 360</div>',
    `<div><a href="mailto:naima@residentservice360.bedmaps.com" style="${linkStyle}">naima@residentservice360.bedmaps.com</a></div>`,
    `<div><a href="https://residentservice360.bedmaps.com" style="${linkStyle}">residentservice360.bedmaps.com</a></div>`,
    '</td>',
    '</tr>',
    '</table>',
  ].join('');
}

/**
 * `{ from, subject, text, html }` for the first email to one housing provider.
 *  - `organisation`: the provider's name.
 *  - `council`: its councils as the plan lists them ("Birmingham, Dudley"); the first one is the area the email names.
 *  - `selfReferral`: true when the provider says it takes self-referrals, which changes the middle paragraph.
 */
export function buildFirstEmail({ organisation, council, selfReferral } = {}) {
  const name = String(organisation ?? '');
  const area = areaOf(council);
  const ask = selfReferral
    ? 'Could we send you referrals? Before we do, we check the person\'s ID and make sure the room is still free, and we\'d follow your usual referral and assessment process.'
    : 'Could we send you referrals? Before we do, we check the person\'s ID and make sure the room is still free, and we\'d refer people to you through your usual process, using your referral form if you have one.';
  const paragraphs = [
    'Hi there,',
    `I'm Naima from Resident Service 360. People looking for supported accommodation come to us and refer themselves, and some of them are looking in ${area}.`,
    selfReferral ? `I saw that ${name} takes self-referrals, so I thought it was worth asking. ${ask}` : ask,
    'Do you have capacity at the moment, or any rooms coming up in the next few weeks? And who\'s the best person to send referrals to?',
  ];
  const signOff = ['Thanks,', 'Naima'];

  const text = [
    ...paragraphs,
    signOff.join('\n'),
  ].join('\n\n')
    + '\n\nNaima\nHousing partnerships · Resident Service 360\nnaima@residentservice360.bedmaps.com\nresidentservice360.bedmaps.com'
    + '\n\nResident Service 360 is a service of Aston Metrics Ltd, registered in England and Wales, company no. 17114120.'
    + `\n\n${OPT_OUT}`;

  const body = [
    ...paragraphs.map((paragraph) => `<p style="${paragraphStyle}">${escapeHtml(paragraph)}</p>`),
    `<p style="${paragraphStyle}">${signOff.map(escapeHtml).join('<br>')}</p>`,
    signatureHtml(),
    // The zero-width non-joiners stop phones turning the company number into a phone link.
    '<div style="margin:18px 0 0;padding-top:10px;border-top:1px solid #E4E1EA;font:11px/1.4 Arial,Helvetica,sans-serif;color:#8C8696;">Resident Service 360 is a service of Aston Metrics Ltd, registered in England and Wales, company no. 171&zwnj;14&zwnj;120.</div>',
    `<p style="margin:18px 0 0;font:12px/1.4 Arial,Helvetica,sans-serif;color:#8C8696;">${OPT_OUT}</p>`,
  ].join('');

  const html = '<!doctype html><html><head><meta charset="utf-8"><meta name="format-detection" content="telephone=no,date=no,address=no,email=no,url=no"></head>'
    + `<body><div style="max-width:640px;">${body}</div></body></html>`;

  return { from: FROM, subject: `Referrals for ${name}`, text, html };
}
